import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Science as ScienceIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Home as HomeIcon,
  VolumeUp as VolumeUpIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  useStartTrialLabTestMutation,
  useSubmitTrialLabAnswerMutation,
  useCompleteTrialLabTestMutation,
} from "@/store/api/user/trialLabApi";
import {
  QuestionRenderer,
  RichTextDisplay,
  DialogueGroupExerciseRenderer,
  PreviewAudioButton,
  AnswerReviewDialog,
  normalizeTrialLabResult,
} from "@/components/common";
import QuestionImage from "@/pages/user/ExamSession/QuestionImage";
import { delay } from "@/utils/common";
import QuestionFeedback from "./QuestionFeedback";

const isMeaningfulAnswer = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.some((v) => isMeaningfulAnswer(v));
  if (typeof value === "object") {
    const values = Object.values(value);
    if (values.length === 0) return false;
    return values.some((v) => isMeaningfulAnswer(v));
  }
  return true;
};

const buildTrialLabRestoredAnswers = (groups = [], responses = []) => {
  const parentIds = new Set();
  const childToParent = {};

  groups.forEach((group) => {
    if (group?.type !== "dialogue-group") return;
    const parentId = String(group?.dialogueQuestion?._id || "");
    if (!parentId) return;
    parentIds.add(parentId);
    (group.childQuestions || []).forEach((child) => {
      if (child?._id) childToParent[String(child._id)] = parentId;
    });
  });

  const restored = {};
  (responses || []).forEach((r) => {
    const qid = String(r?.question?._id || r?.question || "");
    if (!qid) return;
    const selectedOption = r?.selectedOption;

    if (parentIds.has(qid)) {
      const key = `dialogue_${qid}`;
      const prev = restored[key] || {};
      restored[key] = {
        ...prev,
        blanks:
          selectedOption && typeof selectedOption === "object"
            ? selectedOption
            : {},
        childQuestions: prev.childQuestions || {},
      };
      return;
    }

    const parentId = childToParent[qid];
    if (parentId) {
      const key = `dialogue_${parentId}`;
      const prev = restored[key] || {};
      restored[key] = {
        ...prev,
        blanks: prev.blanks || {},
        childQuestions: {
          ...(prev.childQuestions || {}),
          [qid]: selectedOption,
        },
      };
      return;
    }

    restored[qid] = selectedOption;
  });

  return restored;
};

const findTrialLabResumeGroupIndex = (groups = [], responses = []) => {
  const responseByQuestionId = new Map();
  (responses || []).forEach((r) => {
    const qid = String(r?.question?._id || r?.question || "");
    if (!qid) return;
    responseByQuestionId.set(qid, r?.selectedOption);
  });

  for (let gi = 0; gi < groups.length; gi++) {
    const g = groups[gi] || {};
    const ids =
      g.type === "dialogue-group"
        ? [
            String(g?.dialogueQuestion?._id || ""),
            ...(g.childQuestions || []).map((q) => String(q?._id || "")),
          ].filter(Boolean)
        : (g.questions || []).map((q) => String(q?._id || "")).filter(Boolean);

    const allAnswered =
      ids.length > 0 &&
      ids.every((qid) => isMeaningfulAnswer(responseByQuestionId.get(qid)));
    if (!allAnswered) return gi;
  }

  return Math.max(0, groups.length - 1);
};

// ---------------------------------------------------------------------------
// TrialLabTest — group-based practice session
// Mirrors ExerciseSessionInline / LCExerciseSession / RCExerciseSession
// ---------------------------------------------------------------------------

const TrialLabTest = () => {
  const { testIndex } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Audio ──────────────────────────────────────────────────────────────
  const audioRef = useRef(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);

  // ── Tracking refs ──────────────────────────────────────────────────────
  const questionStartTimeRef = useRef({});
  const answersRef = useRef({});

  // ── Core state ─────────────────────────────────────────────────────────
  const [sessionId, setSessionId] = useState(null);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isStarting, setIsStarting] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  // Per-question feedback: { [questionId]: { isCorrect, correctAnswer, explanation } }
  const [feedbacks, setFeedbacks] = useState({});

  // ── API hooks ──────────────────────────────────────────────────────────
  const [startTest] = useStartTrialLabTestMutation();
  const [submitAnswer] = useSubmitTrialLabAnswerMutation();
  const [completeTest] = useCompleteTrialLabTestMutation();

  // ── Filter body from URL search params ────────────────────────────────
  const filterBody = useMemo(() => {
    const body = {};
    const sources = searchParams.get("sources");
    const minIncorrectCount = searchParams.get("minIncorrectCount");
    const minIncorrectRate = searchParams.get("minIncorrectRate");
    const testSize = searchParams.get("testSize");
    if (sources) body.sources = sources.split(",");
    if (minIncorrectCount) body.minIncorrectCount = parseInt(minIncorrectCount);
    if (minIncorrectRate) body.minIncorrectRate = parseInt(minIncorrectRate);
    if (testSize) body.testSize = parseInt(testSize);
    return body;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Start / resume session ─────────────────────────────────────────────
  useEffect(() => {
    const initSession = async () => {
      try {
        const response = await startTest({
          testIndex: parseInt(testIndex),
          ...filterBody,
        }).unwrap();

        const {
          attempt,
          questionGroups: groups,
          isResume,
        } = response.data;

        setSessionId(attempt.sessionId);
        setQuestionGroups(groups || []);

        // Restore previously submitted answers when resuming
        if (isResume && attempt.responses?.length) {
          const restored = buildTrialLabRestoredAnswers(
            groups || [],
            attempt.responses || []
          );
          setAnswers(restored);
          answersRef.current = restored;

          const resumeGroupIndex = findTrialLabResumeGroupIndex(
            groups || [],
            attempt.responses || []
          );
          setCurrentGroupIndex(resumeGroupIndex);
        }

        if (isResume) toast.info("Resuming your previous session");
      } catch (err) {
        console.error("Failed to start test:", err);
        toast.error("Failed to start test session");
      } finally {
        setIsStarting(false);
      }
    };

    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep answersRef in sync
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // ── Derived state ──────────────────────────────────────────────────────
  const currentGroup = questionGroups[currentGroupIndex] || null;
  const isLastGroup = currentGroupIndex === questionGroups.length - 1;
  const isDialogueGroup = currentGroup?.type === "dialogue-group";
  const isLCGroup = !!currentGroup?.audioUrl;

  // Flat list of answerable question IDs in the current group
  const currentGroupQuestionIds = useMemo(() => {
    if (!currentGroup) return [];
    if (isDialogueGroup) {
      const parentId = currentGroup.dialogueQuestion?._id;
      const childIds = (currentGroup.childQuestions || []).map((c) => c._id);
      return parentId ? [parentId, ...childIds] : childIds;
    }
    return (currentGroup.questions || []).map((q) => q._id);
  }, [currentGroup, isDialogueGroup]);

  // Flat list of user-facing answerable questions across all groups.
  // Dialogue PARENTS are intentionally excluded — the parent is a passage with
  // shared fill-blanks (already numbered inline as (1)..(N)), not a standalone
  // "Question N" item. Only standalone questions and dialogue CHILDREN are
  // numbered in the bottom pill bar.
  const flatQuestionList = useMemo(() => {
    const list = [];
    questionGroups.forEach((group, groupIdx) => {
      if (group.type === "dialogue-group") {
        (group.childQuestions || []).forEach((child) => {
          list.push({ questionId: child._id, groupIndex: groupIdx });
        });
      } else {
        (group.questions || []).forEach((q) => {
          list.push({ questionId: q._id, groupIndex: groupIdx });
        });
      }
    });
    return list;
  }, [questionGroups]);

  // Map: questionId → 1-based display number (matches the bottom pill bar).
  const questionNumberMap = useMemo(() => {
    const map = {};
    flatQuestionList.forEach((item, idx) => {
      map[item.questionId] = idx + 1;
    });
    return map;
  }, [flatQuestionList]);

  const totalDisplayQuestions = flatQuestionList.length;

  // Count answered questions across all groups. Handles both standalone
  // questions (`answers[qId]`) and dialogue children
  // (`answers['dialogue_<parentId>'].childQuestions[childId]`).
  const answeredCount = useMemo(() => {
    let count = 0;
    questionGroups.forEach((group) => {
      if (group.type === "dialogue-group") {
        const parent = group.dialogueQuestion;
        const dialogueAns = answers[`dialogue_${parent?._id}`] || {};
        const childAns = dialogueAns.childQuestions || {};
        (group.childQuestions || []).forEach((c) => {
          const v = childAns[c._id];
          if (v !== undefined && v !== null && v !== "") count++;
        });
      } else {
        (group.questions || []).forEach((q) => {
          const v = answers[q._id];
          if (v !== undefined && v !== null && v !== "") count++;
        });
      }
    });
    return count;
  }, [answers, questionGroups]);

  const progress = totalDisplayQuestions > 0
    ? (answeredCount / totalDisplayQuestions) * 100
    : 0;

  // ── Audio auto-play on group change ───────────────────────────────────
  const playAudio = useCallback(async (src) => {
    if (!src || !audioRef.current) return;
    try {
      audioRef.current.pause();
      audioRef.current.src = src;
      if (process.env.NODE_ENV === "development") audioRef.current.playbackRate = 2;
      await new Promise((r) => setTimeout(r, 100));
      await audioRef.current.play();
    } catch (e) {
      console.error("Audio play error:", e);
    }
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.pause();
    if (!isLCGroup || !currentGroup?.audioUrl || isCompleted) return;
    (async () => {
      await delay(600);
      playAudio(currentGroup.audioUrl);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroupIndex, isLCGroup, isCompleted]);

  // Track question start times when group changes
  useEffect(() => {
    currentGroupQuestionIds.forEach((id) => {
      if (!questionStartTimeRef.current[id]) {
        questionStartTimeRef.current[id] = Date.now();
      }
    });
  }, [currentGroupQuestionIds]);

  // ── Answer selection ───────────────────────────────────────────────────
  const handleAnswerSelect = useCallback(
    async (questionId, answer, questionType) => {
      const prevAnswers = answersRef.current;
      setAnswers((prev) => {
        const updated = { ...prev, [questionId]: answer };
        answersRef.current = updated;
        return updated;
      });

      if (!questionStartTimeRef.current[questionId]) {
        questionStartTimeRef.current[questionId] = Date.now();
      }

      if (!sessionId) return;

      // Auto-submit multiple-choice immediately (same as exercise sessions)
      if (questionType === "multiple-choice") {
        const timeSpent = Math.floor(
          (Date.now() - (questionStartTimeRef.current[questionId] || Date.now())) / 1000
        );
        try {
          const result = await submitAnswer({
            sessionId,
            questionId,
            selectedOption: answer,
            timeSpent,
          }).unwrap();
          setFeedbacks((prev) => ({ ...prev, [questionId]: result.data }));
        } catch (err) {
          console.error("Auto-submit error:", err);
        }
        return;
      }

      // Auto-submit dialogue children individually as soon as the user picks
      // an option. The parent's blank fills are NOT auto-submitted here —
      // those are still submitted as a group on Next/Finish.
      if (questionType === "dialogue-group") {
        const prev = prevAnswers[questionId] || {};
        const next = answer || {};
        const prevChildren = prev.childQuestions || {};
        const nextChildren = next.childQuestions || {};
        const changedChildId = Object.keys(nextChildren).find(
          (cid) => nextChildren[cid] !== prevChildren[cid]
        );
        if (
          changedChildId &&
          nextChildren[changedChildId] != null &&
          nextChildren[changedChildId] !== ""
        ) {
          if (!questionStartTimeRef.current[changedChildId]) {
            questionStartTimeRef.current[changedChildId] = Date.now();
          }
          const timeSpent = Math.floor(
            (Date.now() - questionStartTimeRef.current[changedChildId]) / 1000
          );
          try {
            const result = await submitAnswer({
              sessionId,
              questionId: changedChildId,
              selectedOption: nextChildren[changedChildId],
              timeSpent,
            }).unwrap();
            setFeedbacks((prev) => ({ ...prev, [changedChildId]: result.data }));
          } catch (err) {
            console.error("Auto-submit (dialogue child) error:", err);
          }
        }
      }
    },
    [sessionId, submitAnswer]
  );

  // ── Submit all pending answers in the current group ────────────────────
  const submitCurrentGroup = useCallback(async () => {
    if (!sessionId || !currentGroup) return;

    const latestAnswers = answersRef.current;

    if (isDialogueGroup) {
      const parent = currentGroup.dialogueQuestion;
      if (parent) {
        const dialogueKey = `dialogue_${parent._id}`;
        const dialogueAnswers = latestAnswers[dialogueKey] || {};
        const timeSpent = Math.floor(
          (Date.now() - (questionStartTimeRef.current[parent._id] || Date.now())) / 1000
        );
        try {
          const result = await submitAnswer({
            sessionId,
            questionId: parent._id,
            selectedOption: dialogueAnswers.blanks || {},
            timeSpent,
          }).unwrap();
          setFeedbacks((prev) => ({ ...prev, [parent._id]: result.data }));
        } catch (e) {
          console.error("Dialogue blank submit error:", e);
        }
      }

      for (const child of currentGroup.childQuestions || []) {
        const dialogueAnswers = latestAnswers[`dialogue_${parent?._id}`] || {};
        const childAnswer = dialogueAnswers.childQuestions?.[child._id] ?? null;
        const timeSpent = Math.floor(
          (Date.now() - (questionStartTimeRef.current[child._id] || Date.now())) / 1000
        );
        try {
          const result = await submitAnswer({
            sessionId,
            questionId: child._id,
            selectedOption: childAnswer,
            timeSpent,
          }).unwrap();
          setFeedbacks((prev) => ({ ...prev, [child._id]: result.data }));
        } catch (e) {
          console.error("Dialogue child submit error:", e);
        }
      }
    } else {
      for (const question of currentGroup.questions || []) {
        const { _id: qId, type } = question;
        const answer = latestAnswers[qId];
        // MC was already auto-submitted on selection — skip if answered
        if (type === "multiple-choice" && answer) continue;
        const timeSpent = Math.floor(
          (Date.now() - (questionStartTimeRef.current[qId] || Date.now())) / 1000
        );
        try {
          const result = await submitAnswer({
            sessionId,
            questionId: qId,
            selectedOption: answer ?? null,
            timeSpent,
          }).unwrap();
          setFeedbacks((prev) => ({ ...prev, [qId]: result.data }));
        } catch (e) {
          console.error("Submit error:", e);
        }
      }
    }
  }, [sessionId, currentGroup, isDialogueGroup, submitAnswer]);

  // ── Navigation ─────────────────────────────────────────────────────────
  const handleNext = useCallback(async () => {
    await submitCurrentGroup();
    window.scrollTo(0, 0);
    if (currentGroupIndex < questionGroups.length - 1) {
      setCurrentGroupIndex((i) => i + 1);
    }
  }, [submitCurrentGroup, currentGroupIndex, questionGroups.length]);

  const handlePrevious = useCallback(() => {
    window.scrollTo(0, 0);
    if (currentGroupIndex > 0) setCurrentGroupIndex((i) => i - 1);
  }, [currentGroupIndex]);

  const handleFinish = useCallback(async () => {
    if (!sessionId) return;
    setIsSubmitting(true);
    await submitCurrentGroup();
    try {
      const response = await completeTest(sessionId).unwrap();
      setFinalResult(response.data);
      setIsCompleted(true);
      setShowScoreDialog(true);
      toast.success("Test completed!");
    } catch (err) {
      console.error("Complete test error:", err);
      toast.error("Failed to complete test");
    } finally {
      setIsSubmitting(false);
    }
  }, [sessionId, submitCurrentGroup, completeTest]);

  // ── Score color helper ─────────────────────────────────────────────────
  const getScoreColor = (pct) => {
    if (pct >= 80) return "success.main";
    if (pct >= 60) return "warning.main";
    return "error.main";
  };

  // ── Render a question group's content ─────────────────────────────────
  const renderGroupContent = (group, groupIndex, { disabled = false, showResult = false } = {}) => {
    if (!group) return null;

    const hasAudio = !!group.audioUrl;
    const hasContent = !!group.content;
    const hasImage = !!group.imageUrl;
    const isDialogue = group.type === "dialogue-group";

    const questionsBlock = isDialogue ? (
      <DialogueGroupExerciseRenderer
        group={group}
        answers={answers}
        onAnswerChange={handleAnswerSelect}
        showResult={showResult}
        disabled={disabled}
        getQuestionNumber={(q) => questionNumberMap[q._id]}
        feedbacks={disabled ? undefined : feedbacks}
        renderQuestionFeedback={(_q, fb) =>
          fb ? <QuestionFeedback feedback={fb} /> : null
        }
      />
    ) : (
      <Box>
        {(group.questions || []).map((question, idx) => {
          const fb = feedbacks[question._id];
          const questionNumber = questionNumberMap[question._id] || idx + 1;
          return (
            <Box key={question._id}>
              <QuestionRenderer
                question={question}
                answer={answers[question._id]}
                onAnswerChange={(answer) =>
                  handleAnswerSelect(question._id, answer, question.type)
                }
                showResult={showResult}
                disabled={disabled || !!fb}
                questionNumber={questionNumber}
              />
              {fb && !disabled && (
                <QuestionFeedback feedback={fb} />
              )}
            </Box>
          );
        })}
      </Box>
    );

    // LC group: audio indicator on top, questions below
    if (hasAudio) {
      return (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <PreviewAudioButton
              audioUrl={group.audioUrl}
              onPlayAudio={playAudio}
              audioPlaying={isAudioPlaying}
            />
            <Typography variant="body2" color="text.secondary">
              Audio — plays automatically
            </Typography>
          </Box>
          {hasImage && (
            <Box mb={2}>
              <QuestionImage imageUrl={group.imageUrl} extraImageUrl={group.extraImageUrl} />
            </Box>
          )}
          {questionsBlock}
        </Box>
      );
    }

    // RC group: side-by-side layout (content/image on left, questions on right)
    if (hasContent || hasImage) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          {hasImage && (
            <QuestionImage imageUrl={group.imageUrl} extraImageUrl={group.extraImageUrl} />
          )}
          {hasContent && !hasImage && (
            <Box
              sx={{
                width: { xs: "100%", md: "55%" },
                position: { md: "sticky" },
                top: { md: "20px" },
                alignSelf: "flex-start",
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  maxHeight: "90vh",
                  overflow: "auto",
                }}
              >
                <RichTextDisplay content={group.content} />
              </Paper>
            </Box>
          )}
          <Box
            sx={{
              width: hasContent || hasImage ? { xs: "100%", md: "45%" } : "100%",
            }}
          >
            {questionsBlock}
          </Box>
        </Box>
      );
    }

    // Plain group (no audio / image / content)
    return questionsBlock;
  };

  // ── Loading ────────────────────────────────────────────────────────────
  if (isStarting) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center">
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            Preparing your trial test…
          </Typography>
        </Box>
      </Box>
    );
  }

  if (!isStarting && questionGroups.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">No questions available for this trial test.</Alert>
        <Button
          sx={{ mt: 2 }}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/trial-laboratory")}
        >
          Back to Trial Laboratory
        </Button>
      </Container>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50", py: 2 }}>
      {/* Hidden audio element (auto-play LC audio) */}
      <audio
        ref={audioRef}
        onPlay={() => setIsAudioPlaying(true)}
        onPause={() => setIsAudioPlaying(false)}
        onEnded={() => setIsAudioPlaying(false)}
        preload="auto"
        style={{ display: "none" }}
      />

      <Container maxWidth="xl">
        {/* ── Header bar ──────────────────────────────────────────────── */}
        <Paper
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            background: "linear-gradient(135deg, #0288D1 0%, #01579B 100%)",
            color: "white",
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={() => navigate("/trial-laboratory")}
                sx={{ color: "white" }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
              <ScienceIcon fontSize="small" />
              <Typography variant="h6" fontWeight={700}>
                Trial Test {parseInt(testIndex) + 1}
              </Typography>
            </Box>
            <Chip
              label={`${answeredCount} / ${totalDisplayQuestions} answered`}
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: 600 }}
            />
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 1.5,
              height: 6,
              borderRadius: 3,
              bgcolor: "rgba(255,255,255,0.2)",
              "& .MuiLinearProgress-bar": { borderRadius: 3, bgcolor: "white" },
            }}
          />
        </Paper>

        {/* ── Group label ──────────────────────────────────────────────── */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip
            label={`Group ${currentGroupIndex + 1} of ${questionGroups.length}`}
            color="primary"
            variant="outlined"
          />
          {currentGroup?.partNumber && (
            <Chip label={`Part ${currentGroup.partNumber}`} size="small" variant="outlined" />
          )}
          {isLCGroup && (
            <Chip
              icon={<VolumeUpIcon />}
              label="Listening"
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>

        {/* ── Question group area ──────────────────────────────────────── */}
        <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          {renderGroupContent(currentGroup, currentGroupIndex)}
        </Paper>

        {/* ── Question selector (all questions, indexed 1–N) ──────────── */}
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Questions ({flatQuestionList.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click a number to jump to its group
            </Typography>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center"
               sx={{ maxHeight: 200, overflow: "auto" }}>
            {flatQuestionList.map((item, idx) => {
              const { questionId, groupIndex } = item;
              const isInCurrentGroup = groupIndex === currentGroupIndex;
              const isAnswered = answers[questionId] !== undefined;
              const fb = feedbacks[questionId];
              const isCorrect = fb?.isCorrect;
              const isIncorrect = fb && !fb.isCorrect;

              let bgcolor = "grey.200";
              let color = "text.secondary";
              if (isInCurrentGroup) {
                bgcolor = "#0288D1";
                color = "white";
              } else if (isCorrect) {
                bgcolor = "success.main";
                color = "white";
              } else if (isIncorrect) {
                bgcolor = "error.main";
                color = "white";
              } else if (isAnswered) {
                bgcolor = "success.light";
                color = "white";
              }

              return (
                <Box
                  key={questionId}
                  onClick={async () => {
                    if (groupIndex === currentGroupIndex) return;
                    // Submit pending answers in the current group before
                    // jumping — mirrors the Next button behavior.
                    if (!isCompleted) {
                      await submitCurrentGroup();
                    }
                    setCurrentGroupIndex(groupIndex);
                    window.scrollTo(0, 0);
                  }}
                  title={`Question ${idx + 1} (Group ${groupIndex + 1})`}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    transition: "all 0.2s",
                    bgcolor,
                    color,
                    border: isInCurrentGroup ? "2px solid #0288D1" : "1px solid",
                    borderColor: isInCurrentGroup
                      ? "#0288D1"
                      : isCorrect
                      ? "success.main"
                      : isIncorrect
                      ? "error.main"
                      : isAnswered
                      ? "success.light"
                      : "divider",
                    "&:hover": { transform: "scale(1.15)", boxShadow: 2 },
                  }}
                >
                  {idx + 1}
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* ── Navigation ───────────────────────────────────────────────── */}
        <Box display="flex" justifyContent="space-between" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handlePrevious}
            disabled={currentGroupIndex === 0}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Previous
          </Button>

          {isLastGroup ? (
            <Button
              variant="contained"
              color="success"
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={handleFinish}
              disabled={isSubmitting || isCompleted}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, px: 4 }}
            >
              {isSubmitting ? "Submitting…" : "Finish Test"}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                background: "linear-gradient(135deg, #0288D1 0%, #01579B 100%)",
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Container>

      {/* ── Score Dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={showScoreDialog}
        maxWidth="sm"
        fullWidth
        onClose={() => setShowScoreDialog(false)}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0288D1 0%, #01579B 100%)",
            color: "white",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "1.5rem",
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 60, mb: 1, display: "block", mx: "auto" }} />
          Trial Test Complete!
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          {finalResult && (
            <>
              <Box display="flex" justifyContent="center" my={3}>
                <Box
                  sx={{
                    width: 180,
                    height: 180,
                    borderRadius: "50%",
                    border: "8px solid",
                    borderColor: getScoreColor(finalResult.percentage),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.50",
                  }}
                >
                  <Box textAlign="center">
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      sx={{ color: getScoreColor(finalResult.percentage) }}
                    >
                      {finalResult.percentage}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Score</Typography>
                  </Box>
                </Box>
              </Box>

              <Box display="flex" justifyContent="space-around" mb={3}>
                <Box textAlign="center">
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {finalResult.correctAnswers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Correct</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {finalResult.incorrectAnswers}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Incorrect</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h4" fontWeight="bold" color="text.secondary">
                    {finalResult.unansweredQuestions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Unanswered</Typography>
                </Box>
              </Box>

              <LinearProgress
                variant="determinate"
                value={finalResult.percentage}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  bgcolor: "grey.200",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: getScoreColor(finalResult.percentage),
                    borderRadius: 5,
                  },
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<VisibilityIcon />}
            onClick={() => {
              setShowScoreDialog(false);
              setShowReviewDialog(true);
            }}
          >
            Review Answers
          </Button>
          <Button
            variant="contained"
            fullWidth
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => navigate("/trial-laboratory")}
            sx={{ background: "linear-gradient(135deg, #0288D1 0%, #01579B 100%)" }}
          >
            Back to Lab
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Review Answers Dialog ────────────────────────────────────────── */}
      <AnswerReviewDialog
        open={showReviewDialog}
        onClose={() => setShowReviewDialog(false)}
        title={`Review — Trial Test ${parseInt(testIndex) + 1}`}
        headerChips={
          finalResult
            ? [
                `Score: ${finalResult.correctAnswers}/${finalResult.totalQuestions}`,
                `${finalResult.percentage}%`,
              ]
            : []
        }
        detailedGroups={normalizeTrialLabResult(finalResult)}
        useIndexNumbering={true}
      />
    </Box>
  );
};

export default TrialLabTest;

